import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import type { ApiError } from '../utils/api';
import { LocationDetail } from './LocationDetail';
import { AddLocationModal } from './AddLocationModal';
import type { MentionableEntity } from './RichTextField';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import {
  dispatchMentionableEntitiesChanged,
} from '../utils/mentionable-entities-events';
import { AddItemButton } from './AddItemButton';
import { AnalyzeButton } from './AnalyzeButton';
import { PANEL_BODY_FILL_CLASSNAME } from '../constants/layout';
import { PanelPageHeader } from './PanelPageHeader';
import { HeaderCreateActions } from './HeaderPageActions';
import { LocationsEmptyState } from './LocationsEmptyState';
import { DisabledTooltip } from './EmptyStateSecondaryButton';
import { useDocumentWordCount } from '../hooks/useDocumentWordCount';
import {
  getDocumentAnalyzeTooShortTooltip,
  isDocumentTooShortToAnalyze,
} from '../utils/feedback-analyze-availability';
import { clampEntityRelevance } from '../utils/entity-relevance';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from './ui/dialog';
import { ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME } from './EntityDetailModalLayout';
import { LocationsTable, type LocationsTableRow } from './LocationsTable';

interface Location {
  id: string;
  name: string;
  description?: string;
  narrativeFunction?: string;
  physicalCharacteristics?: string;
  rulesAndLogic?: string;
  historicalCorrection?: string;
  improvements?: string;
  isManual?: boolean;
  userNotes?: string;
  relevance?: number;
}

interface LocationsProps {
  documentId?: string;
  projectId?: string;
  isSidebar?: boolean;
  onLocationSelect?: (locationId: string) => void;
  onClose?: () => void;
  initialLocationId?: string;
}

const stringifyField = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value)
      .map(([key, val]) => `${key}: ${typeof val === 'string' ? val : JSON.stringify(val)}`)
      .join('. ');
  }
  return String(value);
};

async function throwUnlessLocationCreditLimit(
  response: Response,
  fallbackMessage: string
): Promise<boolean> {
  const errorData: ApiError = await response.json().catch(() => ({
    error: fallbackMessage,
  }));
  if (response.status === 403 && errorData.errorCode === 'CREDIT_LIMIT_REACHED') {
    return true;
  }
  throw new Error(errorData.error || fallbackMessage);
}

export const Locations: React.FC<LocationsProps> = ({ documentId: propDocumentId, projectId: propProjectId, isSidebar = false, onLocationSelect, onClose, initialLocationId }) => {
  const params = useParams<{ projectId?: string; documentId: string }>();
  const routeDocumentId = params.documentId;
  const routeProjectId = params.projectId;
  const documentId = propDocumentId || routeDocumentId;
  const projectId = propProjectId || routeProjectId;
  const { token } = useAuth();
  const { t } = useLocale();
  const [masterKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('masterKey');
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzingLocationId, setAnalyzingLocationId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const documentWordCount = useDocumentWordCount(documentId, token, masterKey);
  const isAnalyzeDisabled = isDocumentTooShortToAnalyze(documentWordCount);
  const analyzeTooShortTooltip =
    documentWordCount !== null && isAnalyzeDisabled
      ? getDocumentAnalyzeTooShortTooltip(t)
      : undefined;
  const analyzeLocationsFromDocumentTooltip = t('analyze_locations_from_document_tooltip');
  const analyzeTooltip = analyzeTooShortTooltip ?? analyzeLocationsFromDocumentTooltip;

  useEffect(() => {
    const fetchMentionableEntities = async (targetProjectId: string): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${targetProjectId}/mentionable-entities`, {
          headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
          dispatchMentionableEntitiesChanged(targetProjectId);
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };

    const run = async (): Promise<void> => {
      if (!token && !masterKey) return;

      if (projectId) {
        fetchMentionableEntities(projectId);
        return;
      }

      if (!documentId) return;

      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}`;
      const res = await fetch(url, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) return;
      const data = await res.json();
      const docProjectId = data.projectId;
      if (docProjectId) {
        fetchMentionableEntities(docProjectId);
      } else {
        setMentionableEntities([]);
      }
    };

    run();
  }, [projectId, documentId, token, masterKey]);

  useEffect(() => {
    if (!showAddModal && !selectedLocationId) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (showAddModal) {
          setShowAddModal(false);
        }
        if (selectedLocationId) {
          setSelectedLocationId(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAddModal, selectedLocationId]);


  useEffect(() => {
    const abortController = new AbortController();

    const fetchLocations = async (): Promise<void> => {
      if ((!token && !masterKey) || !documentId) {
        setLoading(false);
        return;
      }

      const pollLocations = async (): Promise<void> => {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/locations?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/locations`;

        let timeoutId: NodeJS.Timeout | null = null;

        try {
          timeoutId = setTimeout(() => {
            if (!abortController.signal.aborted) {
              abortController.abort();
            }
          }, 60000);

          const response = await fetch(url, {
            headers: {
              ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: abortController.signal,
          });

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          if (response.status === 202) {
            const data = await response.json();
            if (data.status === 'processing') {
              setTimeout(() => {
                if (!abortController.signal.aborted) {
                  pollLocations();
                }
              }, 2000);
              return;
            }
          }

          if (!response.ok) {
            throw new Error('Failed to fetch locations');
          }

          const data = await response.json();

          let locationsData: Location[] = [];

          if (Array.isArray(data)) {
            locationsData = data;
          } else if (data.locations) {
            locationsData = data.locations;
          } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
            const keys = Object.keys(data);
            if (keys.length > 0 && typeof data[keys[0]] === 'object') {
              locationsData = Object.entries(data).map(([name, loc]: [string, any]) => {
                if (typeof loc === 'object' && loc !== null) {
                  return {
                    id: loc.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
                    name: name || loc.name || '',
                    ...loc,
                  };
                }
                return {
                  id: `temp-${Math.random().toString(36).substr(2, 9)}`,
                  name,
                  description: String(loc),
                };
              });
            }
          }

          if (!Array.isArray(locationsData)) {
            console.error('Locations data is not an array after normalization:', locationsData);
            console.error('Original data:', data);
            locationsData = [];
          }

          locationsData = locationsData.map((loc) => ({
            id: loc.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
            name: String(loc.name || ''),
            description: stringifyField(loc.description),
            narrativeFunction: stringifyField(loc.narrativeFunction),
            physicalCharacteristics: stringifyField(loc.physicalCharacteristics),
            rulesAndLogic: stringifyField(loc.rulesAndLogic),
            historicalCorrection: stringifyField(loc.historicalCorrection),
            improvements: stringifyField(loc.improvements),
            isManual: typeof loc.isManual === 'boolean' ? loc.isManual : undefined,
            relevance: clampEntityRelevance(loc.relevance),
          }));

          locationsData = locationsData.filter((loc): loc is Location =>
            typeof loc === 'object' && loc !== null && 'name' in loc && 'id' in loc
          );

          if (!Array.isArray(locationsData)) {
            locationsData = [];
          }

          setLocations(locationsData);
          setLoading(false);
          setError(null);
        } catch (fetchError) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (fetchError instanceof Error && fetchError.name === 'AbortError') {
            return;
          }
          throw fetchError;
        }
      };

      const pollWithRetry = async (retryCount = 0): Promise<void> => {
        try {
          await pollLocations();
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }

          if (retryCount < 3) {
            const delay = Math.min(2000 * Math.pow(2, retryCount), 10000);
            setTimeout(() => {
              if (!abortController.signal.aborted) {
                pollWithRetry(retryCount + 1);
              }
            }, delay);
            return;
          }

          console.error('Error fetching locations after retries:', err);
          setError('Error al cargar las ubicaciones. Por favor, intenta de nuevo.');
          setLoading(false);
        }
      };

      try {
        setLoading(true);
        setError(null);
        await pollWithRetry();
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching locations:', err);
        setError('Error al cargar las ubicaciones');
        setLoading(false);
      }
    };

    fetchLocations();

    return () => {
      abortController.abort();
    };
  }, [token, documentId, masterKey]);

  useEffect(() => {
    if (!initialLocationId || loading || !locations.length) return;
    const exists = locations.some((l) => l.id === initialLocationId);
    if (exists) {
      setSelectedLocationId(initialLocationId);
    }
  }, [initialLocationId, loading, locations]);

  const handleAnalyze = useCallback(async (): Promise<void> => {
    if (loading || isAnalyzeDisabled) return;

    const abortController = new AbortController();
    let regenerationTriggered = false;

    const triggerRegeneration = async (): Promise<void> => {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/locations?regenerate=true&masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/locations?regenerate=true`;

      let timeoutId: NodeJS.Timeout | null = null;

      try {
        timeoutId = setTimeout(() => {
          if (!abortController.signal.aborted) {
            abortController.abort();
          }
        }, 60000);

        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: abortController.signal,
        });

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (!response.ok && response.status !== 202) {
          if (await throwUnlessLocationCreditLimit(response, 'Failed to trigger location regeneration')) {
            setLoading(false);
            return;
          }
        }

        regenerationTriggered = true;

        if (response.status === 202) {
          const data = await response.json();
          if (data.status === 'processing') {
            setTimeout(() => {
              if (!abortController.signal.aborted) {
                pollStatus();
              }
            }, 2000);
            return;
          }
        }

        const data = await response.json();
        processLocationsData(data);
      } catch (fetchError) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          return;
        }
        throw fetchError;
      }
    };

    const pollStatus = async (): Promise<void> => {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/locations?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/locations`;

      let timeoutId: NodeJS.Timeout | null = null;

      try {
        timeoutId = setTimeout(() => {
          if (!abortController.signal.aborted) {
            abortController.abort();
          }
        }, 60000);

        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: abortController.signal,
        });

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch locations status');
        }

        const data = await response.json();
        const locationsCount = Array.isArray(data.locations) ? data.locations.length : (Array.isArray(data) ? data.length : 0);
        const currentLocationsCount = Array.isArray(locations) ? locations.length : 0;

        if (locationsCount > currentLocationsCount || (locationsCount > 0 && currentLocationsCount === 0)) {
          processLocationsData(data);
        } else {
          setTimeout(() => {
            if (!abortController.signal.aborted) {
              pollStatus();
            }
          }, 2000);
        }
      } catch (fetchError) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          return;
        }
        throw fetchError;
      }
    };

    const processLocationsData = (data: any): void => {
      let locationsData: Location[] = [];

      if (Array.isArray(data)) {
        locationsData = data;
      } else if (data.locations) {
        locationsData = data.locations;
      } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
        const keys = Object.keys(data);
        if (keys.length > 0 && typeof data[keys[0]] === 'object') {
          locationsData = Object.entries(data).map(([name, loc]: [string, any]) => {
            if (typeof loc === 'object' && loc !== null) {
              return { name, ...loc };
            }
            return { name, description: String(loc) };
          });
        }
      }

      if (!Array.isArray(locationsData)) {
        locationsData = [];
      }

      locationsData = locationsData.map((loc) => ({
        id: loc.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
        name: String(loc.name || ''),
        description: stringifyField(loc.description),
        narrativeFunction: stringifyField(loc.narrativeFunction),
        physicalCharacteristics: stringifyField(loc.physicalCharacteristics),
        rulesAndLogic: stringifyField(loc.rulesAndLogic),
        historicalCorrection: stringifyField(loc.historicalCorrection),
        improvements: stringifyField(loc.improvements),
        isManual: typeof loc.isManual === 'boolean' ? loc.isManual : undefined,
        relevance: clampEntityRelevance(loc.relevance),
      }));

      setLocations(locationsData);
      setLoading(false);
      setError(null);
    };

    const pollRegenerateWithRetry = async (retryCount = 0): Promise<void> => {
      try {
        if (!regenerationTriggered) {
          await triggerRegeneration();
        } else {
          await pollStatus();
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        if (retryCount < 3) {
          const delay = Math.min(2000 * Math.pow(2, retryCount), 10000);
          setTimeout(() => {
            if (!abortController.signal.aborted) {
              pollRegenerateWithRetry(retryCount + 1);
            }
          }, delay);
          return;
        }

        console.error('Error regenerating locations after retries:', err);
        setError('Error al regenerar las ubicaciones. Por favor, intenta de nuevo.');
        setLoading(false);
      }
    };

    try {
      setLoading(true);
      setError(null);
      await pollRegenerateWithRetry();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error regenerating locations:', err);
      setError('Error al regenerar las ubicaciones');
      setLoading(false);
    }
  }, [loading, isAnalyzeDisabled, masterKey, documentId, token, locations]);

  const handleAnalyzeLocation = useCallback(
    async (location: LocationsTableRow): Promise<void> => {
      if (analyzingLocationId === location.id) {
        return;
      }

      const abortController = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      try {
        setAnalyzingLocationId(location.id);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/locations/${location.id}/analyze?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/locations/${location.id}/analyze`;

        timeoutId = setTimeout(() => {
          if (!abortController.signal.aborted) {
            abortController.abort();
          }
        }, 120000);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: abortController.signal,
        });

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (!response.ok) {
          if (await throwUnlessLocationCreditLimit(response, 'Failed to analyze location')) {
            return;
          }
        }

        const data = await response.json();
        const updatedLocation = data.location;

        setLocations((prev) =>
          prev.map((loc) =>
            loc.id === location.id
              ? {
                  ...loc,
                  historicalCorrection: updatedLocation.historicalCorrection,
                  improvements: updatedLocation.improvements,
                  relevance: clampEntityRelevance(updatedLocation.relevance) ?? loc.relevance,
                }
              : loc
          )
        );
      } catch (err) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (err instanceof Error && err.name === 'AbortError') {
          setError(t('analysis_timed_out'));
        } else {
          console.error('Error analyzing location:', err);
          setError(t('failed_to_analyze_location'));
        }
      } finally {
        setAnalyzingLocationId(null);
      }
    },
    [analyzingLocationId, documentId, masterKey, t, token]
  );

  const handleLocationRowClick = useCallback(
    (location: LocationsTableRow): void => {
      if (onLocationSelect) {
        onLocationSelect(location.id);
        return;
      }
      setSelectedLocationId(location.id);
    },
    [onLocationSelect]
  );

  if (loading) {
    if (isSidebar) {
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ textAlign: 'center' }}>{t('loading')}</div>
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>{t('loading')}</div>
    );
  }

  if (error) {
    if (isSidebar) {
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ color: '#ef4444', textAlign: 'center' }}>{error}</div>
        </div>
      );
    }
    return (
      <div>
        <PanelPageHeader
          onClose={onClose}
          closeAriaLabel={t('close')}
        />
        <div style={{ color: '#ef4444', textAlign: 'center', padding: '3rem' }}>{error}</div>
      </div>
    );
  }

  const addLocationButton = (
    <AddItemButton
      onClick={() => setShowAddModal(true)}
      label={t('add_location') || 'Add location'}
    />
  );
  const analyzeLocationsButton = (
    <DisabledTooltip enabled={isAnalyzeDisabled} text={analyzeTooShortTooltip}>
      <AnalyzeButton
        className="mr-0"
        onClick={() => { void handleAnalyze(); }}
        isAnalyzing={loading}
        disabled={isAnalyzeDisabled || loading}
        tooltip={isAnalyzeDisabled ? undefined : analyzeLocationsFromDocumentTooltip}
      >
        {t('analyze_locations_from_document')}
      </AnalyzeButton>
    </DisabledTooltip>
  );

  const content = (
    <div className={`${PANEL_BODY_FILL_CLASSNAME} overflow-auto`} style={isSidebar ? { padding: '1rem' } : {}}>
      {!isSidebar && (
        <>
          <HeaderCreateActions hasItems={locations.length > 0}>
            {analyzeLocationsButton}
            {addLocationButton}
          </HeaderCreateActions>
          <PanelPageHeader
            onClose={onClose}
            closeAriaLabel={t('close')}
          />
        </>
      )}
      {isSidebar && locations.length > 0 && (
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {analyzeLocationsButton}
          {addLocationButton}
        </div>
      )}
      {(() => {
          if (!Array.isArray(locations)) {
            console.error('RENDER: locations is not an array!', locations);
            return (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>
                <p style={{ fontSize: '1.125rem' }}>Error: Formato de datos inválido</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Por favor, recarga la página
                </p>
              </div>
            );
          }

          if (locations.length === 0) {
            return (
              <LocationsEmptyState
                canEdit
                onAdd={() => setShowAddModal(true)}
                onAnalyze={() => { void handleAnalyze(); }}
                isAnalyzing={loading}
                analyzeDisabled={isAnalyzeDisabled}
                analyzeTooltip={analyzeTooltip}
              />
            );
          }

          return (
            <LocationsTable
              locations={locations}
              analyzingLocationId={analyzingLocationId}
              onRowClick={handleLocationRowClick}
              onAnalyzeLocation={(location) => {
                void handleAnalyzeLocation(location);
              }}
              analyzeTooltip={t('analyze_location_tooltip')}
            />
          );
        })()}
    </div>
  );

  const locationDetailModal = (
    <Dialog
      open={Boolean(selectedLocationId)}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedLocationId(null);
        }
      }}
    >
      {selectedLocationId && (
        <DialogContent
          className={ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            {locations.find((loc) => loc.id === selectedLocationId)?.name || ''}
          </DialogTitle>
          <LocationDetail
            documentId={documentId}
            projectId={projectId}
            locationId={selectedLocationId}
            onClose={() => setSelectedLocationId(null)}
            isModal={true}
            onUpdated={(updatedLocation) => {
              setLocations((prev) =>
                prev.map((loc) =>
                  loc.id === updatedLocation.id
                    ? { ...loc, name: updatedLocation.name, userNotes: updatedLocation.userNotes }
                    : loc
                )
              );
            }}
            onDeleted={(deletedLocationId) => {
              setLocations((prev) => prev.filter((loc) => loc.id !== deletedLocationId));
              setSelectedLocationId(null);
            }}
          />
        </DialogContent>
      )}
    </Dialog>
  );

  const addLocationModal = (
    <AddLocationModal
      open={showAddModal}
      onOpenChange={setShowAddModal}
      documentId={documentId}
      projectId={projectId}
      mentionableEntities={mentionableEntities}
      onCreated={(location) => {
        setLocations((prev) => [location, ...prev]);
      }}
      onError={(message) => setError(message)}
    />
  );

  if (isSidebar) {
    return (
      <>
        {content}
        {addLocationModal}
        {locationDetailModal}
      </>
    );
  }

  return (
    <>
      {content}
      {addLocationModal}
      {locationDetailModal}
    </>
  );
};

